import { fintracStrAddressFields } from 'app/modules/fintracStr/formConfig/definitions/common/address';
import { fintracStrTelephoneNumberFields } from 'app/modules/fintracStr/formConfig/definitions/common/telephoneNumber';
import {
  FINTRAC_STR_COUNTRY_CODE_OPTIONS,
  FINTRAC_STR_DATE_FORMAT,
  FINTRAC_STR_DEFINITION_TYPE_CODE,
} from 'app/modules/fintracStr/formConstants';
import { getDependentPaths } from 'app/modules/fintracStr/formUtils';
import {
  FintracFormFieldConditional,
  FintracFormSingleField,
  FintracFormValue,
} from 'app/modules/fintracStr/models';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';
import { formatDate } from 'app/shared/utils/date';
import { parseFintracDate } from 'app/modules/fintracStr/utils';
import { subYears } from 'date-fns';

const isPersonDetailsDefinition = (value: FintracFormValue) =>
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_DETAILS.value ||
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_AND_EMPLOYER_DETAILS.value;

const isOnlyPersonDetailsDefinition = (value: FintracFormValue) =>
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_DETAILS.value;

const isPersonAndEmployerDetailsDefinition = (value: FintracFormValue) =>
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.PERSON_AND_EMPLOYER_DETAILS.value;

const isPersonDetailsHideCondition: FintracFormFieldConditional = {
  or: [
    {
      path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
      conditions: (value) => !isPersonDetailsDefinition(value),
    },
  ],
};

const isPersonAndEmployerDetailsHideCondition: FintracFormFieldConditional = {
  or: [
    {
      path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
      conditions: (value) => !isPersonAndEmployerDetailsDefinition(value),
    },
  ],
};

export const FINTRAC_STR_DEFINITIONS_PERSONS_COMPLEX_FIELDS: FintracFormSingleField[] =
  [
    // Following needed for person details and person and employer details
    {
      type: U21FormFieldTypes.TEXT,
      name: 'alias',
      label: 'Alias',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isPersonDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isPersonDetailsHideCondition,
    },
    {
      type: U21FormFieldTypes.DATE_PICKER,
      name: 'dateOfBirth',
      label: 'Date of birth',
      mustExistInPayload: false,
      mustHaveValueIfExist: true,
      fieldProps: {
        minDate: subYears(new Date(), 120),
        maxDate: new Date(),
      },
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isPersonDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isPersonDetailsHideCondition,
      payloadToFormValueConversion: (parentPath, flattenedPayloadValue) => {
        const dateValue = flattenedPayloadValue[`${parentPath}.dateOfBirth`];
        const parsed = parseFintracDate(
          typeof dateValue === 'string' ? dateValue : undefined,
        );
        return { dateOfBirth: parsed };
      },
      formToPayloadValueConversion: (values) => {
        if (values instanceof Date) {
          return { dateOfBirth: formatDate(values, FINTRAC_STR_DATE_FORMAT) };
        }
        return {};
      },
    },
    [
      {
        type: U21FormFieldTypes.TEXT,
        name: 'occupation',
        label: 'Occupation',
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            [isPersonDetailsHideCondition],
            undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: isPersonDetailsHideCondition,
      },

      // Below are fields unique to Person Details only
      {
        type: U21FormFieldTypes.TEXT,
        name: 'nameOfEmployer',
        label: 'Name of employer',
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            [`definitions[${sectionIndex}].typeCode`],
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
              conditions: (value) => !isOnlyPersonDetailsDefinition(value),
            },
          ],
        },
      },
    ],
    {
      type: U21FormFieldTypes.SELECT,
      name: 'countryOfResidenceCode',
      label: 'Country of residence',
      fieldProps: {
        options: FINTRAC_STR_COUNTRY_CODE_OPTIONS,
      },
      mustExistInPayload: false,
      mustHaveValueIfExist: true,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isPersonDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isPersonDetailsHideCondition,
    },

    // Below are fields unique to Person and Employer Details only
    {
      type: U21FormFieldTypes.SELECT,
      name: 'countryOfCitizenshipCode',
      label: 'Country of citizenship',
      fieldProps: {
        options: FINTRAC_STR_COUNTRY_CODE_OPTIONS,
      },
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isPersonAndEmployerDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isPersonAndEmployerDetailsHideCondition,
    },
    {
      type: 'section',
      repeatable: false,
      label: 'Employer Information',
      name: 'employerInformation',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      fields: [
        {
          type: U21FormFieldTypes.TEXT,
          name: 'name',
          label: 'Name',
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              [isPersonAndEmployerDetailsHideCondition],
              undefined,
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: isPersonAndEmployerDetailsHideCondition,
        },
        ...fintracStrTelephoneNumberFields(
          (sectionIndex) => `definitions[${sectionIndex}].employerInformation`,
          isPersonAndEmployerDetailsHideCondition,
        ),
        ...fintracStrAddressFields(
          (sectionIndex) => `definitions[${sectionIndex}].employerInformation`,
          isPersonAndEmployerDetailsHideCondition,
        ),
      ],
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isPersonAndEmployerDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isPersonAndEmployerDetailsHideCondition,
    },
  ];
